import { tableCellStyles } from '../../../hooks/tableStylesHook'
import { formatPercentOrEmpty, formatPriceOrEmpty } from '../../../common/numberUtils'
import { emDash } from '../../../common/stringUtility'
import { createColumns, MyColumnParam } from '../../../types/columnGroupCreator'
import { WbProductColumnId } from '../../../types/wbColumns'
import { formatDateOnly, formatDateTime } from '../../../common/dateTimeUtility'
import { WbProductModel } from '../../../server/mpsklad_core/Models/WbProductModel'
import { IMpCardProduct } from '../../../server/mpsklad_core/Models/MpCard/IMpCardProduct'
import { ExternalProductModel } from '../../../server/mpsklad_core/Models/ExternalProductModel'
import { YandexMarketProductColumnId } from '../../../types/yandexMarketColumns'
import { YandexMarketProductModel } from '../../../server/mpsklad_core/Models/YandexMarketProductModel'
import { OzonProductColumnId } from '../../../types/ozonColumns'
import { OzonProductModel } from '../../../server/mpsklad_core/Models/OzonProductModel'
import { ProductCardPublicationStatus } from '../../../server/mpsklad_core/Models/MpCard/ProductCardPublicationStatus'
import { TableActionsCell } from '../../widgets/productsTable/components/tableActionsCell'
import { MoySkladProductColumnId } from '../../../types/moySkladColumns'
import { MoySkladProductModel } from '../../../server/mpsklad_core/Models/MoySkladProductModel'
import { msProductTypeNames } from '../../../store/logic/productUtils'
import sum from 'lodash/sum'
import { formatBalance } from '../../../common/numberUtils'



//Should the barcodes and discount fields be included in IMpCardProduct?
interface BaseProductModel extends IMpCardProduct, ExternalProductModel {
  barcodes: string[];

  discountPercentage?: number;
}

const unifiedColumns = [
  {
    id: 'barcodes',
    title: 'Штрихкод',
    cellStyle: tableCellStyles.noWrap
  },
  {
    id: 'category',
    title: 'Категория'
  },
  {
    id: 'name',
    title: 'Название'
  },
  {
    id: 'basePrice',
    title: 'Старая цена',
    accessor: _ => formatPriceOrEmpty(_.basePrice),
    cellStyle: tableCellStyles.noWrap
  },
  {
    id: 'discountPercentage',
    title: 'Скидка',
    accessor: _ => formatPercentOrEmpty(_.discountPercentage) || emDash
  },
  {
    id: 'price',
    title: 'Цена',
    accessor: _ => formatPriceOrEmpty(_.price),
    cellStyle: tableCellStyles.noWrap
  },
  {
    id: 'cardPublicationStatus',
    title: 'АПИ СТАТУС',
    accessor: (item: BaseProductModel) => {
      switch (item.cardPublicationStatus) {
        case ProductCardPublicationStatus.NotPublished:
          return 'Не опубликовано'
        case ProductCardPublicationStatus.Processing:
          return 'В обработке'
        case ProductCardPublicationStatus.Moderating:
          return 'На модерации'
        case ProductCardPublicationStatus.Published:
          return 'Опубликовано'
        case ProductCardPublicationStatus.ApiError:
          return 'Ошибка API'
        case ProductCardPublicationStatus.SuccessImport:
          return 'Обновлено'
        default:
          return 'Неизвестный статус'
      }
    }
  },
  {
    id: 'apiMessage',
    title: 'ОШИБКА ОТ АПИ'
  },
  {
    id: 'lastSyncDate',
    title: 'Дата последней синхронизации',
    accessor: _ => {
      if (!_.lastSyncDate) return emDash
      const d = new Date(_.lastSyncDate + 'Z')
      return isNaN(d.getTime()) ? emDash : formatDateTime(d)
    }
  },
  {
    id: 'createdCardDate',
    title: 'Дата создания',
    accessor: _ => {
      if (!_.createdCardDate) return emDash
      const d = new Date(_.createdCardDate)
      return isNaN(d.getTime()) ? emDash : formatDateOnly(d)
    }
  }
] satisfies Array<MyColumnParam<keyof BaseProductModel, BaseProductModel>>

export const wbColumns =
  createColumns<WbProductColumnId, WbProductModel>(
    {
      id: 'supplierArticle',
      title: 'Артикул'
    }, ...unifiedColumns, {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      id: 'actions' as WbProductColumnId,
      title: 'Действия',
      accessor: () => undefined,
      CellComponent: TableActionsCell
    })

export const ymColumns =
  createColumns<YandexMarketProductColumnId, YandexMarketProductModel>(
    {
      id: 'shopSku',
      title: 'Артикул'
    }, ...unifiedColumns, {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      id: 'actions' as YandexMarketProductColumnId,
      title: 'Действия',
      accessor: () => undefined,
      CellComponent: TableActionsCell
    })

export const msColumns =
  createColumns<MoySkladProductColumnId, MoySkladProductModel>(
    {
      id : 'moySkladId',
      title: 'GUID',
      cellStyle: tableCellStyles.noWrap
    },
    {
      id: 'code',
      title: 'Код'
    },
    {
      id: 'article',
      title: 'Артикул'
    },
    {
      id: 'type',
      title: 'Тип',
      accessor: _ => msProductTypeNames[_.type]
    },
    {
      id: 'barcodes',
      title: 'Штрихкоды',
      cellStyle: tableCellStyles.noWrap
    },
    {
      id: 'name',
      title: 'Название',
    },
    {
      id: 'basePrice',
      title: 'Старая цена',
      accessor: _ => formatPriceOrEmpty(_.basePrice),
      cellStyle: tableCellStyles.noWrap
    },
    {
      id: 'price',
      title: 'Цена',
      accessor: _ => formatPriceOrEmpty(_.price),
      cellStyle: tableCellStyles.noWrap
    },
    {
      id: 'balance',
      title: 'Остатки на сматченных складах',
      accessor: row => row.balance.map(_ => `${_.storeName}: ${formatBalance(_.balance)}`)
    },
    {
      id: 'balanceTotal',
      title: 'Общий остаток',
      accessor: row => formatBalance(sum(row.balance.map(_ => _.balance)))
    },
    {
      id: 'cardPublicationStatus',
      title: 'АПИ СТАТУС',
      accessor: (item: MoySkladProductModel) => {
        switch (item.cardPublicationStatus) {
          case ProductCardPublicationStatus.NotPublished:
            return 'Не опубликовано'
          case ProductCardPublicationStatus.Processing:
            return 'В обработке'
          case ProductCardPublicationStatus.Moderating:
            return 'На модерации'
          case ProductCardPublicationStatus.Published:
            return 'Опубликовано'
          case ProductCardPublicationStatus.ApiError:
            return 'Ошибка API'
          case ProductCardPublicationStatus.SuccessImport:
            return 'Обновлено'
          default:
            return 'Неизвестный статус'
        }
      }
    },
    {
      id: 'apiMessage',
      title: 'ОШИБКА ОТ АПИ'
    },
    {
      id: 'lastSyncDate',
      title: 'Дата последней синхронизации',
      accessor: _ => {
        if (!_.lastSyncDate) return emDash
        const d = new Date(_.lastSyncDate + 'Z')
        return isNaN(d.getTime()) ? emDash : formatDateTime(d)
      }
    }

  )

export const ozonColumns =
  createColumns<OzonProductColumnId, OzonProductModel>(
    {
      id: 'article',
      title: 'Артикул'
    }, ...unifiedColumns, {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      id: 'actions' as OzonProductColumnId,
      title: 'Действия',
      accessor: () => undefined,
      CellComponent: TableActionsCell
    })